'use client';

import { QRCodeCanvas } from 'qrcode.react';
import { ReactNode } from 'react';

import { UserTierStatus } from '@flipster/apis/v1/user/types';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import { V2OrderSideType } from '@flipster/utils/types/base';

import { LeverageBadge, OrderSideBadge } from '../../misc/badge';
import { SymbolDisplay } from '../../misc/display';
import { FlipsterLogo } from '../logo';
import { useCheckSpotOnly } from '../regulation';
import { useRawSnapshotSpec } from '../snapshot/use-snapshot-spec';
import { UserTierBadge } from '../user-tier/badge';
import BasicBackground from './background/basic.svg';
import VipTier1Background from './background/vip-tier1.svg';
import VipTier2Background from './background/vip-tier2.svg';
import VipTier3Background from './background/vip-tier3.svg';
import { getVipTierBand, shouldShowReferralSection, shouldShowVipBadge } from './utils';

const tierBackgrounds = {
  basic: BasicBackground,
  tier1: VipTier1Background,
  tier2: VipTier2Background,
  tier3: VipTier3Background,
} as const;

/** 공유 카드는 328x410 고정 사이즈 (QR footer 포함) */
export const cardSize = {
  width: 328,
  height: 410,
} as const;

export type PnlSharePosition = PnlSharePerpetualPosition | PnlShareSpotPosition;

interface PnlShareBasePosition {
  symbol: string;
  status: 'open' | 'closed';
  entryPrice: string | null; // TODO(hoyong): Average Cost BE 배포되면 추후 수정 필요
  evaluationPrice: string;
  pnl: string;
  pnlPercentage: string;
}

export interface PnlSharePerpetualPosition extends PnlShareBasePosition {
  type: 'perpetual';
  side: V2OrderSideType;
  initLeverage: number;
  // only for logging purpose
  profitBoostMode?: boolean;
}

export interface PnlShareSpotPosition extends PnlShareBasePosition {
  type: 'spot';
}

interface Props {
  selected: string;
  copyUrl: string;
  referralCode?: string;
  radioItems?: {
    label: ReactNode;
    value: string;
  }[];
  position: PnlSharePosition;
  /** basic 유저는 0, VIP 유저는 1 이상 */
  vipTier?: number;
  tierStatus?: UserTierStatus;
}

export const PnlShareCard = ({ selected, copyUrl, referralCode, radioItems, position, vipTier, tierStatus }: Props) => {
  const { instrumentsKeyBySymbol } = useRawSnapshotSpec();
  const isSpotOnly = useCheckSpotOnly();

  const selectedLabel = radioItems?.find((t) => t.value === selected)?.label;
  const symbolInfo = instrumentsKeyBySymbol?.[position.symbol];
  const isVip = shouldShowVipBadge(vipTier);
  const TierBackground = tierBackgrounds[getVipTierBand(vipTier)];

  return (
    <div
      className={flex({
        direction: 'column',
        w: cardSize.width,
        h: cardSize.height,
        overflow: 'hidden',
        rounded: 20,
        mt: 16,
      })}
    >
      <div
        className={css({
          pos: 'relative',
          flex: 1,
          minH: 0,
          p: '24px',
          overflow: 'hidden',
          backgroundColor: 'background.default.level-0',
        })}
      >
        {/*
         * 배경 svg는 328x336(referral footer가 있는 기준) 고정이라, VARA처럼 footer가 빠져 높이가 커지면 slice로 채움
         * 이때 좌하단을 기준(xMin/YMax)으로 잘라야 번개 끝이 좌하단 모서리에 맞음
         */}
        <TierBackground
          className={css({ pos: 'absolute', inset: 0, w: '100%', h: '100%' })}
          preserveAspectRatio='xMinYMax slice'
        />
        <div className={css({ pos: 'relative' })}>
          <div className={hstack({ justifyContent: 'space-between', alignItems: 'center', mb: 24 })}>
            <FlipsterLogo withType css={{ w: 83 }} />
            {isVip && <UserTierBadge size='medium' status={tierStatus} tier={vipTier} />}
          </div>
          <div
            className={hstack({
              alignItems: 'center',
              mb: 12,
              gap: 8,
            })}
          >
            <SymbolDisplay
              symbol={position.symbol}
              icon={<SymbolDisplay.Icon size={24} />}
              mainLabel={
                <SymbolDisplay.Label
                  value='shortName'
                  css={{ color: 'content.default.level-5', textStyle: 'Title/XS' }}
                />
              }
            />
            {position.type === 'perpetual' && (
              <div
                className={hstack({
                  alignItems: 'center',
                  gap: 4,
                })}
              >
                <OrderSideBadge orderSide={position.side} />
                <LeverageBadge leverage={position.initLeverage} />
              </div>
            )}
          </div>
          <p className={descStyles}>{selectedLabel}</p>
          {selected === 'roi' && (
            <Formatted.Percent
              value={position.pnlPercentage}
              css={{ textStyle: 'Display/XL' }}
              withColor
              withPlus
              precision={UsdtPrecision}
              toFixed
            />
          )}
          {selected === 'pnl' && (
            <Formatted.Number
              value={position.pnl}
              css={{
                textStyle: 'Display/XL',
              }}
              withColor
              withPlus
              precision={UsdtPrecision}
              toFixed
            />
          )}
          <dl
            className={vstack({
              mt: 24,
              gap: 12,
              alignItems: 'flex-start',
            })}
          >
            <div>
              <dt className={descStyles}>
                <FormattedMessage id='Assets.r250401.Entry_price' />
              </dt>
              <dd className={priceStyles}>
                <Formatted.Number
                  css={{ textStyle: 'Text/L/Strong' }}
                  value={position.entryPrice}
                  precision={symbolInfo?.pricePrecision}
                  toFixed
                />
              </dd>
            </div>
            <div>
              <dt className={descStyles}>
                <FormattedMessage
                  id={position.status === 'open' ? 'Assets.r250401.Current_price' : 'Assets.r250401.Close_price'}
                />
              </dt>
              <dd className={priceStyles}>
                <Formatted.Number
                  css={{ textStyle: 'Text/L/Strong' }}
                  value={position.evaluationPrice}
                  precision={symbolInfo?.pricePrecision}
                  toFixed
                />
              </dd>
            </div>
          </dl>
        </div>
      </div>
      {shouldShowReferralSection(isSpotOnly) && (
        <div
          className={flex({
            justifyContent: 'space-between',
            p: 16,
            color: 'inverse.black',
            backgroundColor: 'white',
          })}
        >
          <div className={vstack({ gap: 4, alignItems: 'flex-start' })}>
            <p
              className={css({
                textStyle: 'Text/S/Regular',
              })}
            >
              <FormattedMessage id='Referral.r260120.Share_card_notice' />
            </p>
            <h2
              className={css({
                textStyle: 'Title/XS',
              })}
            >
              <FormattedMessage id='Referral.r250401.Share_card_referral_code_colon' />
              {referralCode}
            </h2>
          </div>
          <QRCodeCanvas value={copyUrl} size={42} />
        </div>
      )}
    </div>
  );
};

const descStyles = css({
  textStyle: 'Text/S/Regular',
  mb: 2,
  color: 'content.default.level-1',
});

const priceStyles = css({
  color: 'content.default.level-4',
  textStyle: 'Title/XS',
});
